import { operationalCalendar } from '../shared/campaigns';
import { useState } from 'react';
import {
  ArrowRight,
  CalendarDays,
  CheckCheck,
  ClipboardList,
  CircleAlert,
  FileCheck2,
  Clock3,
  Flag,
  Link2,
  RefreshCw,
  Users,
} from 'lucide-react';
import type { Campaign, Content, State } from '../shared/model';
import { stageLabels } from '../shared/model';
import type { WorkQueue, WorkQueueItem } from '../shared/work-queue';
import { metaLabel } from './Meta';

interface Props {
  data: State;
  workQueue?: WorkQueue;
  openWorkItem: (item: WorkQueueItem) => void;
  busy: boolean;
  openContent: (item: Content) => void;
  openCampaign: (item: Campaign) => void;
  navigate: (page: 'calendar' | 'content' | 'campaigns' | 'connections') => void;
  showUnassigned: () => void;
  refreshArchive: () => void;
}
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / 86400000);
const shortDate = (date: string) =>
  date
    ? new Date(`${date}T12:00:00`)
        .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
        .replace('.', '')
    : 'Sem data';

export function Dashboard({
  data,
  workQueue,
  openWorkItem,
  busy,
  openCampaign,
  navigate,
  refreshArchive,
}: Props) {
  const dateOf = (content: Content) =>
    operationalCalendar(data).find((e) => e.contentId === content.id)?.date ?? '';
  const production = data.contents.filter(
    (c) => !['publicado', 'medido', 'descartado'].includes(c.stage),
  );
  const active = data.contents.filter((c) => c.stage !== 'descartado');
  const publishedIds = new Set(data.publications.map((publication) => publication.contentId));
  const published = active.filter((content) => publishedIds.has(content.id));
  const planned = production
    .filter((c) => dateOf(c))
    .sort((a, b) => dateOf(a).localeCompare(dateOf(b)));
  const missingOwner = production.filter((c) => !c.owner);
  const openProposals = data.proposals.filter((p) => ['pendente', 'conflito'].includes(p.status));
  const activeCampaigns = data.campaigns.filter((campaign) => campaign.status === 'ativa');
  const campaigns = [...data.campaigns]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, 4);
  const dueThisWeek = planned.filter(
    (c) =>
      !c.reserve &&
      daysBetween(dateOf(c), data.today) >= 0 &&
      daysBetween(dateOf(c), data.today) < 7,
  );
  const progress = active.length ? (published.length / active.length) * 100 : 0;

  return (
    <>
      <div className="stats operational-stats">
        <Metric
          label="Peças com publicação"
          value={published.length}
          total={active.length || undefined}
          caption={
            active.length
              ? 'Com registro por conta · sem descartadas'
              : 'Crie ou vincule a primeira peça'
          }
          progress={active.length ? progress : undefined}
        />
        <Metric label="Em produção" value={production.length} caption="Da pauta à edição" />
        <Metric
          label="Próximos 7 dias"
          value={dueThisWeek.length}
          caption="Entregas previstas · sem reservas"
        />
        <Metric
          label="Sem responsável"
          value={missingOwner.length}
          caption="Conteúdos em produção"
        />
        <Metric
          label="Propostas abertas"
          value={openProposals.length}
          caption="Mudanças de calendário"
        />
        <Metric
          label="Campanhas ativas"
          value={activeCampaigns.length}
          caption="Campanhas em operação"
        />
      </div>
      <WorkQueuePanel queue={workQueue} onOpen={openWorkItem} />
      <div className="operations-grid">
        <div className="operations-column">
          <section className="panel operation-panel production-summary">
            <div className="section-heading">
              <h2>Produção por etapa</h2>
              <button className="text-button" onClick={() => navigate('content')}>
                Abrir quadro <ArrowRight size={14} />
              </button>
            </div>
            <div className="stage-summary">
              {(['pauta', 'roteiro', 'gravado', 'editado'] as const).map((stage) => (
                <div key={stage}>
                  <span>
                    <i className={`stage-dot ${stage}`} />
                    {stageLabels[stage]}
                  </span>
                  <strong>{production.filter((c) => c.stage === stage).length}</strong>
                </div>
              ))}
            </div>
            <p className="section-caption">
              Reserva é uma marcação da peça e mantém sua etapa de produção.
            </p>
          </section>
          <section className="panel operation-panel connection-summary">
            <div className="section-heading">
              <h2>
                <Link2 size={17} /> Meta · {metaLabel(data.integration)}
              </h2>
            </div>
            <p className="section-caption">
              {data.integration.identity
                ? `${data.integration.accounts?.length ?? 0} contas autorizadas. Consulte as leituras na aba Métricas.`
                : 'Conecte seu perfil para ler contas e métricas autorizadas.'}
            </p>
            <button className="text-button" onClick={() => navigate('connections')}>
              Ver conexões <ArrowRight size={13} />
            </button>
          </section>
        </div>
        <div className="operations-column">
          <section className="panel operation-panel campaign-summary">
            <div className="section-heading">
              <h2>Campanhas recentes</h2>
              <button className="text-button" onClick={() => navigate('campaigns')}>
                Ver todas <ArrowRight size={14} />
              </button>
            </div>
            {campaigns.length ? (
              campaigns.map((c) => {
                const items = data.contents.filter((item) => item.campaignIds.includes(c.id));
                const done = items.filter((item) => publishedIds.has(item.id)).length;
                return (
                  <article key={c.id} className="campaign-summary-item">
                    <div>
                      <strong>{c.name}</strong>
                      <span className={`badge ${c.status === 'ativa' ? 'publicado' : ''}`}>
                        {
                          {
                            planejada: 'Planejada',
                            ativa: 'Ativa',
                            pausada: 'Pausada',
                            encerrada: 'Encerrada',
                          }[c.status]
                        }
                      </span>
                    </div>
                    <p>{c.objective}</p>
                    {items.length ? (
                      <div className="campaign-progress">
                        <span>
                          {done} de {items.length} {items.length === 1 ? 'peça' : 'peças'} com
                          publicação registrada
                        </span>
                        <div>
                          <i
                            style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="campaign-start-hint">
                        Sem conteúdo no planejamento. Abra para acompanhar publicações ou planejar
                        conteúdo.
                      </p>
                    )}
                    <button className="text-button" onClick={() => openCampaign(c)}>
                      Abrir campanha <ArrowRight size={14} />
                    </button>
                  </article>
                );
              })
            ) : (
              <div className="inline-empty">
                <Flag size={22} />
                <div>
                  <strong>Nenhuma campanha criada</strong>
                  <p>Crie uma campanha para organizar objetivos e peças.</p>
                  <button className="text-button" onClick={() => navigate('campaigns')}>
                    Ir para campanhas <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            )}
            {campaigns.length > 0 && (
              <p className="campaign-scope-note">
                Contagem das peças vinculadas à campanha. Publicações por conta e vínculos
                específicos estão em Resultados.
              </p>
            )}
          </section>
        </div>
      </div>
      <div className="source-footer">
        <span className="dot" />
        <span>
          {data.sync
            ? `Acervo conferido em ${new Date(data.sync.createdAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · ${data.sync.sources} fontes`
            : 'Importação pendente'}
        </span>
        <button disabled={busy} className="text-button" onClick={refreshArchive}>
          <RefreshCw size={13} className={busy ? 'spin' : ''} />
          Atualizar acervo
        </button>
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  total,
  caption,
  progress,
}: {
  label: string;
  value: number;
  total?: number;
  caption: string;
  progress?: number;
}) {
  return (
    <section className={`stat ${progress !== undefined ? 'highlight' : ''}`}>
      <div>{label}</div>
      <strong>
        {value}
        {total !== undefined && <span> / {total}</span>}
      </strong>
      <small>{caption}</small>
      {progress !== undefined && (
        <div className="metric-progress">
          <i style={{ width: `${progress}%` }} />
        </div>
      )}
    </section>
  );
}

type QueueFilter = 'all' | 'overdue' | 'today' | 'review' | 'unassigned';
const queueKind = {
  connection: {
    label: 'Conexão',
    action: 'Ver orientação',
    dateLabel: 'Validade informada',
    icon: Link2,
  },
  collection: {
    label: 'Coleta',
    action: 'Abrir resultados',
    dateLabel: 'Coleta de',
    icon: RefreshCw,
  },
  closing: { label: 'D+7', action: 'Abrir resultados', dateLabel: 'D+7 em', icon: Clock3 },
  task: { label: 'Tarefa', action: 'Abrir produção', dateLabel: 'Prazo', icon: ClipboardList },
  automation: {
    label: 'Automação',
    action: 'Conferir automação',
    dateLabel: 'Prazo',
    icon: CircleAlert,
  },
  distribution: {
    label: 'Destinos',
    action: 'Conferir destinos',
    dateLabel: 'Publicação prevista',
    icon: CircleAlert,
  },
  package: {
    label: 'Pacote',
    action: 'Abrir revisão',
    dateLabel: 'Publicação prevista',
    icon: FileCheck2,
  },
  proposal: {
    label: 'Proposta de data',
    action: 'Abrir calendário',
    dateLabel: 'Data proposta',
    icon: CalendarDays,
  },
  content: {
    label: 'Conteúdo',
    action: 'Abrir conteúdo',
    dateLabel: 'Data planejada',
    icon: CircleAlert,
  },
  campaign: { label: 'Campanha', action: 'Abrir campanha', dateLabel: 'Fim previsto', icon: Flag },
};
const urgencyLabel = {
  blocked: 'Requer atenção',
  overdue: 'Data vencida',
  today: 'Hoje',
  upcoming: 'Data futura',
  unscheduled: 'Sem data',
};

function WorkQueuePanel({
  queue,
  onOpen,
}: {
  queue?: WorkQueue;
  onOpen: (item: WorkQueueItem) => void;
}) {
  const [filter, setFilter] = useState<QueueFilter>('all');
  const [limit, setLimit] = useState(8);
  const items = queue?.items ?? [];
  const filtered = items.filter((item) => {
    if (filter === 'overdue') return Boolean(item.dueDate && queue && item.dueDate < queue.asOf);
    if (filter === 'today') return item.dueDate === queue?.asOf;
    if (filter === 'review')
      return item.urgency === 'blocked' || ['package', 'proposal'].includes(item.kind);
    if (filter === 'unassigned') return !item.owner.trim();
    return true;
  });
  return (
    <section className="panel operation-panel work-queue" aria-labelledby="work-queue-heading">
      <div className="section-heading work-queue-heading">
        <div>
          <h2 id="work-queue-heading">
            Fila de trabalho {queue && <span className="count-pill">{items.length}</span>}
          </h2>
          <p className="section-caption">
            Produção, revisões, planejamento, métricas e conexões. Cada ação abre o registro para
            continuar o trabalho.
          </p>
        </div>
        <label className="work-queue-filter">
          <span>Mostrar</span>
          <select
            aria-label="Filtrar fila de trabalho"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as QueueFilter);
              setLimit(8);
            }}
          >
            <option value="all">Todas as pendências</option>
            <option value="overdue">Com data vencida</option>
            <option value="today">Com data hoje</option>
            <option value="review">Revisões e conflitos</option>
            <option value="unassigned">Sem responsável</option>
          </select>
        </label>
      </div>
      {!queue ? (
        <div className="inline-empty">
          <Clock3 size={22} />
          <div>
            <strong>Fila de trabalho ainda não disponível</strong>
            <p>Atualize os dados para consultar as pendências.</p>
          </div>
        </div>
      ) : !filtered.length ? (
        <div className="inline-empty">
          <CheckCheck size={22} />
          <div>
            <strong>
              {items.length ? 'Nenhuma pendência neste filtro' : 'Nenhuma pendência na fila'}
            </strong>
            <p>
              {items.length
                ? 'Escolha outro filtro para consultar o trabalho em aberto.'
                : 'Tarefas abertas, pacotes em revisão e alertas de planejamento aparecerão aqui.'}
            </p>
          </div>
        </div>
      ) : (
        <ul className="work-queue-list">
          {filtered.slice(0, limit).map((item) => {
            const kind = queueKind[item.kind];
            const Icon = kind.icon;
            return (
              <li className={`work-queue-item ${item.urgency}`} key={item.id}>
                <span className="work-queue-icon" aria-hidden="true">
                  <Icon size={18} />
                </span>
                <div className="work-queue-body">
                  <div className="work-queue-title">
                    <span className="work-queue-kind">{kind.label}</span>
                    <strong>{item.title}</strong>
                  </div>
                  <p className="work-queue-context">{item.context}</p>
                  <p className="work-queue-reasons">{item.reasons.join(' · ')}</p>
                  <div className="work-queue-meta">
                    <span>
                      <Users size={12} aria-hidden="true" />
                      {item.owner || 'Responsável a definir'}
                    </span>
                    <span>
                      <CalendarDays size={12} aria-hidden="true" />
                      {item.dueDate
                        ? `${kind.dateLabel} ${shortDate(item.dueDate)}`
                        : item.kind === 'task'
                          ? 'Prazo não definido'
                          : 'Data não definida'}
                    </span>
                  </div>
                </div>
                <div className="work-queue-action">
                  <span className={`work-queue-urgency ${item.urgency}`}>
                    {urgencyLabel[item.urgency]}
                  </span>
                  <button onClick={() => onOpen(item)} aria-label={`${kind.action}: ${item.title}`}>
                    {kind.action}
                    <ArrowRight size={13} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {queue && filtered.length > 0 && (
        <div className="work-queue-foot">
          <span>
            {Math.min(limit, filtered.length)} de {filtered.length}{' '}
            {filtered.length === 1 ? 'pendência' : 'pendências'}
            {filter !== 'all' ? ' neste filtro' : ''} · Datas em São Paulo
          </span>
          {filtered.length > limit && (
            <button className="text-button" onClick={() => setLimit((value) => value + 8)}>
              Mostrar mais <ArrowRight size={13} />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
